/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import {
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import { Subscription } from 'rxjs';
import { VsLogsGraphStore } from './vs-logs-graph.store';
import { VsLogsStore } from '../../services/vs-logs.store';
import { TVsLogsGraphStateParams } from '../../vs-logs.types';
import { VsLogsRefreshService } from '../../services/vs-logs-refresh.service';
import * as l10n from './vs-logs-graph.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *      Main graph for vs logs page.
 *      This component houses the bar-graph at top of vs-logs page, gotten from /logs/analytics
 *      api when request param 'step' is provided. Each bar represents number of logs (y-axis)
 *      for a given time period (x-axis), for a given duration (entire x-axis).
 *      Graph is made by child component avi-bar-graph, this component basically
 *      just handles api calls and passing in data to avi-bar-graph.
 * @author Akul Aggarwal, Alex Klyuev
 */
@Component({
    selector: 'vs-logs-graph',
    templateUrl: './vs-logs-graph.component.html',
    providers: [VsLogsGraphStore],
})
export class VsLogsGraphComponent implements OnInit, OnDestroy {
    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /**
     * Unique label addendum to show for summary row inside tooltip.
     */
    public tooltipLogsLabel: string;

    /**
     * Set subscription to begin API calls.
     */
    private logsGraphApiParamsSubscription: Subscription;

    constructor(
        public readonly vsLogsGraphStore: VsLogsGraphStore,
        public readonly vsLogsStore: VsLogsStore,
        public readonly l10nService: L10nService,
        vsLogsRefreshService: VsLogsRefreshService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        // Subscribe to Refresh service for refresh graph action.
        vsLogsRefreshService.refresh$.subscribe(({ isPageCustomTimeframe }) => {
            if (isPageCustomTimeframe) {
                vsLogsGraphStore.refreshGraph();
            }
        });
    }

    /** @override */
    public ngOnInit(): void {
        /**
         * Subscribe to changes in the logs API parameters. Get new logs graph data upon change.
         */
        // subscribe to the store under ngOnInit instead of the constructor to avoid the unnecessary API call triggered
        // by the emission before the store is first lazy initialized, which happens under the page component ngOnInit
        this.logsGraphApiParamsSubscription =
            this.vsLogsStore.vsLogsGraphApiParams$.subscribe((apiParams: TVsLogsGraphStateParams) => {
                this.vsLogsGraphStore.getVsLogsGraphData(apiParams);
            });

        this.tooltipLogsLabel = this.l10nService.getMessage(globalL10nKeys.logsLabel);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.logsGraphApiParamsSubscription.unsubscribe();
    }
}
